import { themeVar } from '@/styles/theme';
import styled from 'styled-components';

import { KeyboardEvent, useEffect, useState } from 'react';
import { CheckBoxIcon } from './icon';

type CheckBoxProp = {
  isActive: boolean;
  id: string;
  className?: string;
  checkBoxIconSizes?: {
    width: number;
    height: number;
  };
  onClick?: (isChecked?: boolean) => void;
  disabled?: boolean;
  'data-testid'?: string;
};

const CheckBox = ({ isActive, id, className, checkBoxIconSizes = { width: 16, height: 14 }, onClick, disabled = false, 'data-testid': testId }: CheckBoxProp) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setIsChecked(isActive);
  }, [isActive]);

  const handleCheck = () => {
    if (disabled) return;

    const newValue = !isActive;
    setIsChecked(newValue);

    if (onClick) {
      onClick(newValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (onClick) {
        handleCheck();
      } else {
        // Click is often on the wrapper (CheckboxWrapper), not on CheckBox — simulate parent click
        const target = event.currentTarget as HTMLElement;
        target.parentElement?.click();
      }
    }
  };

  return (
    <CheckBoxWrap
      data-testid={testId}
      className={className}
      id={id}
      isActive={isActive}
      onClick={handleCheck}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="checkbox"
      aria-checked={isActive}
      disabled={disabled}
    >
      {isActive && <CheckBoxIcon width={checkBoxIconSizes.width} height={checkBoxIconSizes.height} fill={themeVar('layer.2')} />}
    </CheckBoxWrap>
  );
};

const CheckBoxWrap = styled.div<{ isActive: boolean; disabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  min-width: 24px;
  border-radius: 4px;
  border: 1px solid ${themeVar('border.base')};
  background: ${({ isActive, disabled }) => {
    if (isActive) {
      return disabled ? themeVar('font.disabled') : themeVar('font.base');
    }
    return disabled ? themeVar('action.disabled') : themeVar('layer.2');
  }};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

export default CheckBox;
